import React, { useMemo } from 'react';
import { Product, Rating, Review } from 'types/Product';
import Helmet from 'react-helmet';
import { getBaseSchemaOrgTags } from './getBaseSchemaOrgTags';
import {
  buildPermutationSkuFromValues,
  getProductNameForOptions,
  getProductPriceForOptions,
  getProductUrlForOptions,
  useProduct
} from 'features/pdp';
import { getOptionsStringsForProductVariant, getProductVariants, ItemAvailability, ProductVariant } from 'helpers/seo';
import { PriceObject } from '../../features/pdp/components/ProductDetails/ProductPrice';
import { PageContext } from 'gatsby/internal';

const baseTags = getBaseSchemaOrgTags();

interface SchemaOrgProductProps {
  product: Product;
  pageContext: PageContext;
}

export const SchemaOrgProduct: React.FC<SchemaOrgProductProps> = props => {
  const { product: propsProduct, pageContext } = props;
  const { product: contextProduct } = useProduct();

  const product = contextProduct ?? propsProduct;

  const { markup, snippetCount, merchantListingCount } = useMemo(() =>
      getSchemaOrgValuesForProduct(product, pageContext),
    [product, pageContext]);

  const markupString = useMemo(() =>
      JSON.stringify(markup, null, '  '),
    [markup]);

  return (
    <Helmet>
      <script type="application/ld+json">{markupString}</script>
      <meta name="product-snippet-count" content={snippetCount.toString()} />
      <meta name="merchant-listing-count" content={merchantListingCount.toString()} />
    </Helmet>
  );
};

const getSchemaOrgValuesForProduct = (product: Product, pageContext: PageContext) => {
  const variants = getProductVariants(product);
  const hasVariants = variants.length > 1;

  const snippetCount = 1;
  const merchantListingCount = Math.max(variants.length, 1);
  const markup = hasVariants
    ? getProductGroupMarkup(product, variants, pageContext)
    : getSingleProductMarkup(product, pageContext);

  return {
    markup,
    snippetCount,
    merchantListingCount
  };
};

const getProductGroupMarkup = (product: Product, variants: ProductVariant[], { rating, reviews }: PageContext) => {
  return {
    ...baseTags,
    '@type': 'ProductGroup',
    name: product.name,
    description: product.metaDescription,
    url: product.url,
    image: product.image,
    productGroupID: product.sku,
    ...getProductVariantMarkup(product, variants),
    ...getRatingMarkup(rating),
    ...getReviewMarkup(reviews)
  };
};

const getSingleProductMarkup = (product: Product, { rating, reviews }: PageContext) => {
  const price = getProductPriceForOptions(product, {});
  const isOutOfStock = product?.inStock === false;

  return {
    ...baseTags,
    '@type': 'Product',
    name: product.name,
    description: product.metaDescription,
    image: product.image,
    ...getOffersMarkup(price, product.url, isOutOfStock),
    ...getRatingMarkup(rating),
    ...getReviewMarkup(reviews)
  };
};

const getProductVariantMarkup = (product: Product, variants: ProductVariant[]) => {
  const variesBySet = new Set<string>();

  const hasVariant = variants.map(variant => {
    const { options: variantOptions, price, ...variesByProps } = variant;
    const displayOptions = variantOptions.map(option => option.displayValue);
    const name = getProductNameForOptions(product, displayOptions);

    const image = product.image; // TODO: Try to use unique image per product?

    const options = getOptionsStringsForProductVariant(variant, product.customOptions);
    const url = getProductUrlForOptions(product, Object.values(options));

    const permutation = buildPermutationSkuFromValues(product, product.sku, options);
    const isOutOfStock = product.outOfStockPermutations?.some(key => key === permutation);

    Object.keys({ ...variesByProps }).forEach(prop => variesBySet.add(prop));

    return {
      '@type': 'Product',
      name,
      description: product.metaDescription,
      image,
      ...variesByProps,
      ...getOffersMarkup(price, url, isOutOfStock)
    };
  });

  const variesBy = getVariesByList(variesBySet);
  return {
    variesBy,
    hasVariant
  };
};

const getVariesByList = (set: Set<string>) => [...set].map(category => `https://schema.org/${category}`);

const getOffersMarkup = (price: PriceObject, url?: string, isOutOfStock = false) => {
  const hasStrikethroughPrice = !!price.strikethroughPrice;
  const productPrice = hasStrikethroughPrice ? price.strikethroughPrice : price.productPriceTotal;
  return {
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: productPrice.toFixed(2),
      availability: isOutOfStock ? ItemAvailability.OUT_OF_STOCK : ItemAvailability.IN_STOCK,
      url,
      ...(hasStrikethroughPrice && {
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/ListPrice',
          price: price.productPriceTotal.toFixed(2),
          priceCurrency: 'USD'
        }
      })
    }
  };
};

const getRatingMarkup = (rating?: Rating) => {
  if (!rating) {
    return false;
  }
  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      reviewCount: rating.totalReviews,
      ratingValue: rating.score,
      worstRating: 1,
      bestRating: 5
    }
  };
};

const getReviewMarkup = (reviews?: Review[]) => {
  if (!reviews) {
    return false;
  }
  return {
    review: reviews.map(review => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        worstRating: 1,
        bestRating: 5,
        ratingValue: review.score
      },
      reviewBody: review.content,
      author: {
        '@type': 'Person',
        name: review.user.display_name
      },
      datePublished: review.created_at
    }))
  };
};
